import "webrtc-adapter";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    getBboxDetectionsUrl,
    getThermalDetectionsUrl,
    getWebRTCConfigAddress,
    getWebRTCSignallingAddress,
    isThermalCameraStream,
    pickDefaultStreamingSource,
    streamMatchesInferenceSource,
} from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";
import { getAuthToken } from "lib/utils/auth";
import { StreamingState } from "lib/consts/streaming";
import { fetchInferSource, fetchYoloRecognition } from "lib/api/objectRecognition";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useARC } from "providers/ARC/ARCContext";
import { useOrgFleet } from "./OrgFleetProvider";
import GstWebRTCAPI from "lib/webrtc/vendor/gstwebrtc-api/gstwebrtc-api";
import { stopUnsharedMediaTracks } from "lib/webrtc/mediaStreamLifecycle";
import { buildBrowserIceServers, buildProducerIceServers } from "lib/webrtc/icePolicy";
import { evaluateDecodeStall, pickVideoInboundSample } from "lib/webrtc/decodeStall";
import { createWebRTCDebugTap } from "lib/webrtc/debugTap";
import JanusRollbackProvider, { useJanusWebRTC } from "./JanusRollbackProvider";

export const WebRTCContext = createContext(null);
WebRTCContext.displayName = "WebRTCContext";
export const useWebRTC = () => useContext(WebRTCContext);

const DEFAULT_RECOVERY_GRACE_MS = 20000;
const EMPTY_VIEWER_FAILURES = new Map();
const configuredRecoveryGraceMs = Number(process.env.REACT_APP_WEBRTC_RECOVERY_GRACE_MS);
const RECOVERY_GRACE_MS = Number.isInteger(configuredRecoveryGraceMs)
    && configuredRecoveryGraceMs >= 1000
    && configuredRecoveryGraceMs <= 60000
    ? configuredRecoveryGraceMs
    : DEFAULT_RECOVERY_GRACE_MS;
const SIGNAL_RETRY_INITIAL_MS = 500;
const SIGNAL_RETRY_MAX_MS = 3000;
// The browser gives a stalled WebSocket handshake about a minute; give up sooner
// and let the normal signaling retry path take over.
const SIGNAL_CONNECT_TIMEOUT_MS = 3000;
const SESSION_RETRY_LIMIT = 4;
const VIEWER_CONNECT_TIMEOUT_MS = 30000;
// A connected viewer whose inbound video stats show no usable frames for
// DECODE_STALL_MS is closed and recreated (see lib/webrtc/decodeStall.js).
const DECODE_STATS_INTERVAL_MS = 1000;
const DECODE_STALL_MS = 5000;
const POLL_INTERVAL_MS = 1000 / 15;
const POLL_TIMEOUT_MS = 900;
const WEBRTC_NEGOTIATION_DEBUG = process.env.REACT_APP_WEBRTC_NEGOTIATION_DEBUG === "true";
const logWebRTC = (event, details = {}) => {
    if (WEBRTC_NEGOTIATION_DEBUG) console.info("[rswebrtc]", event, details);
};
const warnWebRTC = (event, details = {}) => {
    if (WEBRTC_NEGOTIATION_DEBUG) console.warn("[rswebrtc]", event, details);
};
const getAuthTokenExpiry = () => {
    const token = getAuthToken();
    if (!token) return null;
    try {
        const encoded = token.split(".")[1];
        const claims = JSON.parse(atob(encoded.replace(/-/g, "+").replace(/_/g, "/")));
        return Number.isFinite(claims.exp) ? new Date(claims.exp * 1000).toISOString() : null;
    } catch {
        return null;
    }
};
const jitteredBackoff = (initialMs, maxMs, attempt) => {
    const exponential = Math.min(initialMs * (2 ** attempt), maxMs);
    return Math.min(maxMs, Math.round(exponential * (0.8 + Math.random() * 0.4)));
};

const getCameraKey = (producer) => {
    const meta = producer?.meta || {};
    const key = meta.stream_key || meta["stream-key"] || meta.cameraKey || meta.camera_key;
    return typeof key === "string" && key.trim() ? key.trim() : null;
};

const normalizeProducer = (producer) => {
    const cameraKey = getCameraKey(producer);
    if (!cameraKey || !producer?.id) return null;
    const meta = producer.meta || {};
    return {
        cameraKey,
        producerId: producer.id,
        displayName: meta.display_name || meta["display-name"] || cameraKey,
        generation: Number(meta.generation) || 0,
        instanceId: meta.instance_id || meta["instance-id"] || null,
        status: StreamingState.None,
        mediaStream: null,
        viewerFailure: null,
        lastEventAt: Date.now(),
        recoveryDeadline: null,
        lastError: null,
    };
};

// Fleet producers use the browser ICE list unless explicitly set to host-only.
// The device validates each ICE request before applying it.
const useLegacyBridge = ({ children }) => {
    const legacy = useJanusWebRTC();
    const value = useMemo(() => ({
        ...legacy,
        viewerFailures: legacy.viewerFailures || EMPTY_VIEWER_FAILURES,
        setWebRTCServerHost: legacy.setJanusServerHost,
        connectedToWebRTC: legacy.connectedToJanus,
        getMediaStream: legacy.getStream,
        signalingError: null,
    }), [legacy]);
    return <WebRTCContext.Provider value={value}>{children}</WebRTCContext.Provider>;
};

/**
 * Select one transport for the whole webapp build. rswebrtc is the default;
 * REACT_APP_WEBRTC_TRANSPORT=janus selects the rollback provider.
 */
const WebRTCProvider = ({ children }) => {
    const transport = process.env.REACT_APP_WEBRTC_TRANSPORT || "rswebrtc";
    if (transport === "janus") {
        return (
            <JanusRollbackProvider>
                <LegacyBridgeContents>{children}</LegacyBridgeContents>
            </JanusRollbackProvider>
        );
    }
    if (transport === "rswebrtc") return <RsWebRTCProvider>{children}</RsWebRTCProvider>;
    return <WebRTCContext.Provider value={{
        setWebRTCServerHost: () => { }, connectedToWebRTC: false,
        availableStreams: [], streamDisplayNames: new Map(), streamStatuses: new Map(), streamVersions: new Map(),
        viewerFailures: EMPTY_VIEWER_FAILURES,
        activeStream: null, signalingError: `Unknown WebRTC transport: ${transport}`,
    }}>{children}</WebRTCContext.Provider>;
};

// The component boundary lets the rollback provider keep its existing private
// context while all application consumers use the neutral WebRTCContext.
const LegacyBridgeContents = ({ children }) => <LegacyBridge>{children}</LegacyBridge>;
const LegacyBridge = ({ children }) => useLegacyBridge({ children });

const RsWebRTCProvider = ({ children }) => {
    const isFleetMode = !!useOrgFleet();
    const isSecure = useIsHttpSecure() || isFleetMode;
    const arcId = useFleetArcId();
    const arcSerial = useARC()?.selectedARC?.serialNumber ?? null;
    const [serverHost, setServerHost] = useState(null);
    const [apiEpoch, setApiEpoch] = useState(0);
    const [sessionRetry, setSessionRetry] = useState(0);
    const [connectedToWebRTC, setConnected] = useState(false);
    const [clientPeerId, setClientPeerId] = useState(null);
    const [catalog, setCatalog] = useState(() => new Map());
    const [activeStream, setActiveStream] = useState(null);
    const [streamVersions, setStreamVersions] = useState(() => new Map());
    const [streamDetections, setStreamDetections] = useState(() => new Map());
    const [objectRecognitionEnabled, setObjectRecognitionEnabled] = useState(false);
    const [inferenceSource, setInferenceSource] = useState("front");
    const [pinnedPayloadStreams, setPinnedPayloadStreams] = useState([]);
    // Capture side of the developer diagnostics; inert until WebRTCDebugProvider enables it.
    const [debugTap] = useState(createWebRTCDebugTap);
    // An effect, so cleanup events from the previous ARC's viewers keep its serial.
    useEffect(() => debugTap.setArcSerial(arcSerial), [debugTap, arcSerial]);
    const [showStreamingSource, setShowStreamingSource] = useState(false);
    const [fitScreen, setFitScreen] = useState(true);
    const [signalingError, setSignalingError] = useState(null);
    const apiRef = useRef(null);
    const consumerSessionsRef = useRef(new Map());
    const retryTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const sessionAttemptRef = useRef(new Map());
    const sessionRetryTimerRef = useRef(new Map());
    const removalTimersRef = useRef(new Map());
    const mediaStreamsRef = useRef(new Map());
    const activeStreamRef = useRef(activeStream);
    activeStreamRef.current = activeStream;

    const closeConsumerSession = useCallback((cameraKey, preserveStatus = false) => {
        const record = consumerSessionsRef.current.get(cameraKey);
        if (!record) return;
        logWebRTC("viewer-close", { cameraKey, producerId: record.producerId, sessionId: record.session?.sessionId || null, preserveStatus, hadMedia: mediaStreamsRef.current.has(cameraKey) });
        record.cancelled = true;
        consumerSessionsRef.current.delete(cameraKey);
        const mediaStream = mediaStreamsRef.current.get(cameraKey);
        mediaStreamsRef.current.delete(cameraKey);
        stopUnsharedMediaTracks(mediaStream);
        if (record.connectTimer) clearTimeout(record.connectTimer);
        if (record.statsTimer) clearInterval(record.statsTimer);
        if (record.debug) record.debug.detach();
        if (record.session) record.session.close();
        setCatalog((previous) => {
            const current = previous.get(cameraKey);
            if (!current || current.producerId !== record.producerId) return previous;
            const status = preserveStatus ? current.status : StreamingState.None;
            if (!current.mediaStream && current.status === status) return previous;
            const next = new Map(previous);
            next.set(cameraKey, { ...current, mediaStream: null, status });
            return next;
        });
    }, []);

    const scheduleSessionRetry = useCallback((cameraKey, message) => {
        const timers = sessionRetryTimerRef.current;
        if (timers.has(cameraKey)) return;
        const attempts = sessionAttemptRef.current;
        const attempt = attempts.get(cameraKey) || 0;
        if (attempt >= SESSION_RETRY_LIMIT) {
            warnWebRTC("viewer-retries-exhausted", { cameraKey, attempts: attempt + 1, reason: message });
            debugTap.report({ kind: "reconnect", phase: "exhausted", cameraKey, attempt: attempt + 1, reason: message });
            setCatalog((previous) => {
                const current = previous.get(cameraKey);
                if (!current) return previous;
                const next = new Map(previous);
                next.set(cameraKey, {
                    ...current,
                    status: current.producerId ? StreamingState.None : current.status,
                    viewerFailure: message,
                });
                return next;
            });
            return;
        }
        attempts.set(cameraKey, attempt + 1);
        const delay = jitteredBackoff(500, 8000, attempt);
        warnWebRTC("viewer-retry-scheduled", { cameraKey, nextAttempt: attempt + 2, delayMs: delay, reason: message });
        debugTap.report({ kind: "reconnect", phase: "scheduled", cameraKey, attempt: attempt + 2, delayMs: delay, reason: message });
        timers.set(cameraKey, window.setTimeout(() => {
            timers.delete(cameraKey);
            logWebRTC("viewer-retry-start", { cameraKey, attempt: attempt + 2 });
            debugTap.report({ kind: "reconnect", phase: "start", cameraKey, attempt: attempt + 2 });
            setSessionRetry((value) => value + 1);
        }, delay));
    }, [debugTap]);

    const availableStreams = useMemo(() => [...catalog.keys()].sort((left, right) => {
        const leftLower = left.toLocaleLowerCase();
        const rightLower = right.toLocaleLowerCase();
        const leftFront = leftLower === "front";
        const rightFront = rightLower === "front";
        if (leftFront !== rightFront) return leftFront ? -1 : 1;
        return leftLower.localeCompare(rightLower) || left.localeCompare(right);
    }), [catalog]);
    const streamDisplayNames = useMemo(() => {
        const names = new Map();
        catalog.forEach((entry, key) => {
            names.set(key, entry.displayName || key);
        });
        return names;
    }, [catalog]);
    const streamStatuses = useMemo(() => {
        const statuses = new Map();
        catalog.forEach((entry, key) => statuses.set(key, entry.status));
        return statuses;
    }, [catalog]);
    const viewerFailures = useMemo(() => {
        const failures = new Map();
        catalog.forEach((entry, key) => {
            if (entry.viewerFailure) failures.set(key, entry.viewerFailure);
        });
        return failures;
    }, [catalog]);
    const inferenceTargetStream = useMemo(
        () => availableStreams.find((name) => streamMatchesInferenceSource(name, inferenceSource)) || null,
        [availableStreams, inferenceSource]
    );

    const refreshInferenceSource = useCallback((source) => {
        setInferenceSource(source === "gimbal" ? "gimbal" : "front");
    }, []);

    const clearStreamDetections = useCallback((key) => {
        setStreamDetections((previous) => {
            if (key) {
                if (!previous.has(key)) return previous;
                const next = new Map(previous);
                next.delete(key);
                return next;
            }
            return previous.size ? new Map() : previous;
        });
    }, []);

    const togglePinnedPayloadStream = useCallback((streamName) => {
        if (!streamName) return;
        setPinnedPayloadStreams((previous) => {
            if (previous.includes(streamName)) return previous.filter((name) => name !== streamName);
            if (streamName === activeStreamRef.current) return previous;
            return [...previous, streamName];
        });
    }, []);

    const swapPinnedWithActive = useCallback((pinnedName) => {
        const previousActive = activeStreamRef.current;
        if (!pinnedName || !previousActive || pinnedName === previousActive) return;
        setActiveStream(pinnedName);
        setPinnedPayloadStreams((previous) => {
            const next = previous.filter((name) => name !== pinnedName);
            return next.includes(previousActive) ? next : [...next, previousActive];
        });
    }, []);

    const getMediaStream = useCallback((cameraKey) => catalog.get(cameraKey)?.mediaStream || null, [catalog]);

    const attachMediaStreamToElem = useCallback((element, cameraKey) => {
        if (!element) return;
        const stream = cameraKey ? catalog.get(cameraKey)?.mediaStream : null;
        if (element.srcObject !== stream) element.srcObject = stream;
        if (stream && element.paused && typeof element.play === "function") {
            element.play()?.catch?.((error) => console.warn("WebRTC video playback did not start", error));
        }
    }, [catalog]);

    const updateEntry = useCallback((cameraKey, update) => {
        setCatalog((previous) => {
            const current = previous.get(cameraKey);
            if (!current) return previous;
            const next = new Map(previous);
            next.set(cameraKey, { ...current, ...update });
            return next;
        });
    }, []);

    const markRemoved = useCallback((producer) => {
        const normalized = normalizeProducer(producer);
        if (!normalized) return;
        const current = catalogRef.current.get(normalized.cameraKey);
        // A delayed remove for an older producer cannot retire its replacement.
        if (!current || current.producerId !== normalized.producerId) return;
        warnWebRTC("producer-removed", { cameraKey: normalized.cameraKey, producerId: normalized.producerId });
        debugTap.report({ kind: "producer", phase: "removed", cameraKey: normalized.cameraKey, producerId: normalized.producerId });
        const deadline = Date.now() + RECOVERY_GRACE_MS;
        updateEntry(normalized.cameraKey, {
            producerId: null,
            mediaStream: null,
            status: StreamingState.Reconnecting,
            lastEventAt: Date.now(),
            recoveryDeadline: deadline,
        });
        const oldTimer = removalTimersRef.current.get(normalized.cameraKey);
        if (oldTimer) clearTimeout(oldTimer);
        removalTimersRef.current.set(normalized.cameraKey, window.setTimeout(() => {
            removalTimersRef.current.delete(normalized.cameraKey);
            setCatalog((previous) => {
                const entry = previous.get(normalized.cameraKey);
                if (!entry || entry.producerId || entry.recoveryDeadline !== deadline) return previous;
                const next = new Map(previous);
                next.delete(normalized.cameraKey);
                return next;
            });
        }, RECOVERY_GRACE_MS));
    }, [updateEntry, debugTap]);

    const catalogRef = useRef(catalog);
    catalogRef.current = catalog;

    const markAdded = useCallback((producer) => {
        const incoming = normalizeProducer(producer);
        if (!incoming) {
            console.warn("Ignoring WebRTC producer without stable stream_key metadata", producer);
            return;
        }
        if (catalogRef.current.get(incoming.cameraKey)?.producerId !== incoming.producerId) {
            logWebRTC("producer-added", { cameraKey: incoming.cameraKey, producerId: incoming.producerId, generation: incoming.generation });
            debugTap.report({ kind: "producer", phase: "added", cameraKey: incoming.cameraKey, producerId: incoming.producerId });
            sessionAttemptRef.current.delete(incoming.cameraKey);
            const retryTimer = sessionRetryTimerRef.current.get(incoming.cameraKey);
            if (retryTimer) clearTimeout(retryTimer);
            sessionRetryTimerRef.current.delete(incoming.cameraKey);
        }
        const timer = removalTimersRef.current.get(incoming.cameraKey);
        if (timer) clearTimeout(timer);
        removalTimersRef.current.delete(incoming.cameraKey);
        setCatalog((previous) => {
            const current = previous.get(incoming.cameraKey);
            if (current?.producerId === incoming.producerId) {
                const next = new Map(previous);
                next.set(incoming.cameraKey, {
                    ...current,
                    ...incoming,
                    mediaStream: current.mediaStream,
                    status: current.mediaStream ? StreamingState.Okay : current.status,
                });
                return next;
            }
            const next = new Map(previous);
            next.set(incoming.cameraKey, incoming);
            return next;
        });
    }, [debugTap]);

    // Establish one listener connection. The API's built-in reconnect loop is
    // disabled so retry limits and terminal errors remain visible to the UI.
    useEffect(() => {
        const connectionHost = serverHost || "";
        // ControlLayout supplies the selected ARC's proxy host after this
        // provider first mounts. No ARC has been selected yet, so there is no
        // signaling failure to report or retry.
        if (!connectionHost.trim()) {
            setConnected(false);
            setSignalingError(null);
            return undefined;
        }
        const signalingUrl = getWebRTCSignallingAddress(connectionHost, arcId);
        if (!signalingUrl) {
            warnWebRTC("signaling-url-missing", { mode: isFleetMode ? "fleet" : "direct", hasProxyHost: true, hasFleetArcId: Boolean(arcId) });
            setSignalingError("WebRTC signaling URL could not be derived for the selected ARC.");
            debugTap.report({ kind: "signaling", phase: "failed", detail: "signaling URL could not be derived for the selected ARC" });
            setConnected(false);
            return undefined;
        }
        let disposed = false;
        const browserIceServers = buildBrowserIceServers(connectionHost, isFleetMode);
        logWebRTC("signaling-connect", { mode: isFleetMode ? "fleet" : "direct", epoch: apiEpoch, browserIceServerCount: browserIceServers.length, iceTransportPolicy: isFleetMode ? "relay" : "all", authTokenPresent: Boolean(getAuthToken()), authTokenExpiresAt: getAuthTokenExpiry() });
        debugTap.report({ kind: "signaling", phase: "connecting", detail: `attempt ${reconnectAttemptRef.current + 1}, host ${connectionHost}, ${isFleetMode ? "fleet" : "direct"}` });
        const api = new GstWebRTCAPI({
            signalingServerUrl: signalingUrl,
            reconnectionTimeout: 0,
            meta: { role: "listener", client: "webapp2.0" },
            // Explicitly set the complete mode-specific browser policy. Never
            // inherit the gstwebrtc-api public STUN defaults accidentally.
            webrtcConfig: {
                iceServers: browserIceServers,
                // Preserve Janus mode's relay-only fleet path. Direct mode
                // keeps host/LAN candidates available.
                iceTransportPolicy: isFleetMode ? "relay" : "all",
            },
        });
        apiRef.current = api;
        const producerListener = { producerAdded: markAdded, producerRemoved: markRemoved };
        let connectTimer = null;
        const connectionListener = {
            connected: (peerId) => {
                if (disposed || apiRef.current !== api) return;
                clearTimeout(connectTimer);
                logWebRTC("signaling-connected", { peerId, producerCount: api.getAvailableProducers().length });
                debugTap.report({ kind: "signaling", phase: "connected", detail: `peer ${peerId}, ${api.getAvailableProducers().length} producers` });
                reconnectAttemptRef.current = 0;
                sessionAttemptRef.current.clear();
                sessionRetryTimerRef.current.forEach((timer) => clearTimeout(timer));
                sessionRetryTimerRef.current.clear();
                setClientPeerId(peerId);
                setConnected(true);
                setSignalingError(null);
                // The initial list is a reconciliation event; subsequent
                // producerAdded/Removed events keep it live without refresh.
                api.getAvailableProducers().forEach(markAdded);
            },
            disconnected: () => {
                if (disposed || apiRef.current !== api) return;
                warnWebRTC("signaling-disconnected", { retryNumber: reconnectAttemptRef.current + 1 });
                debugTap.report({ kind: "signaling", phase: "disconnected", detail: `retry ${reconnectAttemptRef.current + 1}` });
                setConnected(false);
                setClientPeerId(null);
                setCatalog((previous) => {
                    const next = new Map(previous);
                    next.forEach((entry, key) => {
                        if (!entry.producerId) return;
                        const deadline = Date.now() + RECOVERY_GRACE_MS;
                        next.set(key, { ...entry, producerId: null, mediaStream: null, status: StreamingState.Reconnecting, recoveryDeadline: deadline });
                        const oldTimer = removalTimersRef.current.get(key);
                        if (oldTimer) clearTimeout(oldTimer);
                        removalTimersRef.current.set(key, window.setTimeout(() => {
                            setCatalog((currentCatalog) => {
                                const current = currentCatalog.get(key);
                                if (!current || current.producerId || current.recoveryDeadline !== deadline) return currentCatalog;
                                const updated = new Map(currentCatalog);
                                updated.delete(key);
                                return updated;
                            });
                            removalTimersRef.current.delete(key);
                        }, RECOVERY_GRACE_MS));
                    });
                    return next;
                });
                [...consumerSessionsRef.current.keys()].forEach((key) => closeConsumerSession(key, true));
                const attempt = reconnectAttemptRef.current++;
                const delay = jitteredBackoff(SIGNAL_RETRY_INITIAL_MS, SIGNAL_RETRY_MAX_MS, attempt);
                logWebRTC("signaling-retry-scheduled", { nextAttempt: attempt + 2, delayMs: delay });
                debugTap.report({ kind: "signaling", phase: "retry scheduled", detail: `attempt ${attempt + 2} in ${delay} ms` });
                retryTimerRef.current = window.setTimeout(() => setApiEpoch((value) => value + 1), delay);
            },
        };
        api.registerConnectionListener(connectionListener);
        api.registerProducersListener(producerListener);
        connectTimer = window.setTimeout(() => {
            if (disposed || apiRef.current !== api) return;
            warnWebRTC("signaling-connect-timeout", { timeoutMs: SIGNAL_CONNECT_TIMEOUT_MS });
            debugTap.report({ kind: "signaling", phase: "timed out", detail: `no connection after ${SIGNAL_CONNECT_TIMEOUT_MS / 1000} s` });
            // Same handling as a dropped connection (state reset, backoff retry),
            // then abandon the pending socket instead of waiting on the browser.
            connectionListener.disconnected();
            api.close();
        }, SIGNAL_CONNECT_TIMEOUT_MS);

        return () => {
            disposed = true;
            clearTimeout(connectTimer);
            logWebRTC("signaling-close", { epoch: apiEpoch });
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            api.unregisterConnectionListener(connectionListener);
            api.unregisterProducersListener(producerListener);
            if (apiRef.current === api) apiRef.current = null;
            [...consumerSessionsRef.current.keys()].forEach((key) => {
                const current = catalogRef.current.get(key);
                if (current?.producerId) {
                    updateEntry(key, { status: StreamingState.Reconnecting, mediaStream: null });
                }
                closeConsumerSession(key, true);
            });
            api.close();
            setConnected(false);
            setClientPeerId(null);
        };
    }, [serverHost, arcId, isFleetMode, apiEpoch, markAdded, markRemoved, updateEntry, closeConsumerSession, debugTap]);

    // Keep the existing camera active through short producer restarts. Once
    // its grace entry expires, choose the same front-first fallback as Janus.
    useEffect(() => {
        if (activeStream && availableStreams.includes(activeStream)) return;
        if (!availableStreams.length) {
            if (!activeStream) return;
            const entry = catalog.get(activeStream);
            if (entry?.recoveryDeadline) return;
            setActiveStream(null);
            return;
        }
        setActiveStream(pickDefaultStreamingSource(availableStreams) || availableStreams[0]);
    }, [availableStreams, activeStream, catalog]);

    useEffect(() => {
        setPinnedPayloadStreams((previous) => {
            const next = previous.filter((key) => availableStreams.includes(key) && key !== activeStream);
            return next.length === previous.length ? previous : next;
        });
    }, [availableStreams, activeStream]);

    const desiredViewerEntries = useMemo(() => {
        const keys = [...new Set([activeStream, ...pinnedPayloadStreams].filter(Boolean))];
        return keys
            .map((key) => [key, catalog.get(key)])
            .filter(([, entry]) => entry?.producerId);
    }, [activeStream, pinnedPayloadStreams, catalog]);

    // Keep one consumer session for the main view and each pinned preview.
    // These sessions share the signaling connection but keep independent media
    // state and failure recovery.
    useEffect(() => {
        const api = apiRef.current;
        const wanted = new Map(desiredViewerEntries);
        [...consumerSessionsRef.current.entries()].forEach(([cameraKey, record]) => {
            if (!wanted.has(cameraKey) || wanted.get(cameraKey).producerId !== record.producerId) {
                const catalogEntry = catalog.get(cameraKey);
                const preserveRecoveryState = Boolean(
                    catalogEntry && !catalogEntry.producerId && catalogEntry.recoveryDeadline
                );
                closeConsumerSession(cameraKey, preserveRecoveryState);
            }
        });
        if (!api || !clientPeerId || !connectedToWebRTC) return;

        const missing = desiredViewerEntries.filter(([cameraKey, entry]) => {
            const current = consumerSessionsRef.current.get(cameraKey);
            return !sessionRetryTimerRef.current.has(cameraKey)
                && (sessionAttemptRef.current.get(cameraKey) || 0) < SESSION_RETRY_LIMIT
                && (!current || current.producerId !== entry.producerId);
        });
        if (!missing.length) return;

        const records = missing.map(([cameraKey, entry]) => {
            logWebRTC("viewer-start", { cameraKey, producerId: entry.producerId, attempt: (sessionAttemptRef.current.get(cameraKey) || 0) + 1 });
            const record = {
                producerId: entry.producerId,
                session: null,
                cancelled: false,
                connectTimer: null,
                statsTimer: null,
                decodeState: null,
                decodeConfirmed: false,
                observedTracks: new WeakSet(),
            };
            consumerSessionsRef.current.set(cameraKey, record);
            updateEntry(cameraKey, { status: StreamingState.Reconnecting, lastError: null, viewerFailure: null });
            return [cameraKey, record];
        });

        const start = async () => {
            const configUrl = getWebRTCConfigAddress(serverHost, arcId);
            if (!configUrl) throw new Error("WebRTC ICE configuration URL could not be derived.");
            const producerIceServers = buildProducerIceServers(
                serverHost,
                isFleetMode,
                process.env.REACT_APP_WEBRTC_PRODUCER_ICE_POLICY,
            );
            logWebRTC("config-request", { peerId: clientPeerId, sessionCount: records.length, producerIceServerCount: producerIceServers.length });
            // The API's peer ID identifies this browser signaling connection.
            // Include one use for each consumer added below so pinned previews
            // can share the same session-scoped ICE policy safely.
            const response = await authedFetch(configUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    peerId: clientPeerId,
                    iceServers: producerIceServers,
                    sessionCount: records.length,
                }),
            });
            logWebRTC("config-response", { peerId: clientPeerId, status: response.status, accepted: response.ok });
            if (!response.ok) throw new Error(`WebRTC ICE configuration rejected (HTTP ${response.status}).`);
            await Promise.all(records.map(async ([cameraKey, record]) => {
                if (record.cancelled || consumerSessionsRef.current.get(cameraKey) !== record) return;
                const session = api.createConsumerSession(record.producerId);
                if (!session) throw new Error(`Could not create a WebRTC viewer for ${cameraKey}.`);
                record.session = session;
                logWebRTC("viewer-session-created", { cameraKey, producerId: record.producerId });
                const isCurrent = () => !record.cancelled
                    && consumerSessionsRef.current.get(cameraKey) === record
                    && record.session === session;
                record.connectTimer = window.setTimeout(() => {
                    // ontrack may fire as soon as SDP is applied, before ICE
                    // or DTLS succeeds. A MediaStream is not a connected peer.
                    if (!isCurrent() || (session.rtcPeerConnection?.connectionState === "connected" && mediaStreamsRef.current.has(cameraKey))) return;
                    debugTap.report({ kind: "stall", cause: "negotiation-timeout", cameraKey, connectionId: record.debug?.connectionId, connectionState: session.rtcPeerConnection?.connectionState || null, iceConnectionState: session.rtcPeerConnection?.iceConnectionState || null });
                    warnWebRTC("viewer-timeout", { cameraKey, sessionState: session.state, sessionId: session.sessionId || null, connectionState: session.rtcPeerConnection?.connectionState || null, iceConnectionState: session.rtcPeerConnection?.iceConnectionState || null });
                    updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: "Viewer negotiation timed out" });
                    closeConsumerSession(cameraKey, true);
                    scheduleSessionRetry(cameraKey, `WebRTC viewer negotiation timed out for ${cameraKey}.`);
                }, VIEWER_CONNECT_TIMEOUT_MS);
                session.addEventListener("streamsChanged", () => {
                    if (!isCurrent()) return;
                    const stream = session.streams?.[0] || null;
                    if (!stream) return;
                    if (mediaStreamsRef.current.get(cameraKey) === stream) return;
                    logWebRTC("viewer-media", { cameraKey, sessionId: session.sessionId || null, tracks: stream.getTracks().map((track) => ({ kind: track.kind, readyState: track.readyState, muted: track.muted })) });
                    stream.getTracks().forEach((track) => {
                        if (record.observedTracks.has(track)) return;
                        record.observedTracks.add(track);
                        track.addEventListener("ended", () => {
                            if (!isCurrent()) return;
                            debugTap.report({ kind: "stall", cause: "track-ended", cameraKey, connectionId: record.debug?.connectionId, trackKind: track.kind });
                            warnWebRTC("viewer-track-ended", { cameraKey, kind: track.kind });
                            updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: "Media track ended" });
                            session.close();
                        }, { once: true });
                    });
                    stopUnsharedMediaTracks(mediaStreamsRef.current.get(cameraKey), stream);
                    if (session.rtcPeerConnection?.connectionState === "connected") {
                        if (record.connectTimer) clearTimeout(record.connectTimer);
                        record.connectTimer = null;
                    }
                    mediaStreamsRef.current.set(cameraKey, stream);
                    setCatalog((previous) => {
                        const current = previous.get(cameraKey);
                        if (!current || current.producerId !== record.producerId) return previous;
                        const next = new Map(previous);
                        const connected = session.rtcPeerConnection?.connectionState === "connected";
                        next.set(cameraKey, { ...current, mediaStream: stream, status: connected ? StreamingState.Okay : StreamingState.Reconnecting, lastEventAt: Date.now(), recoveryDeadline: connected ? null : current.recoveryDeadline, lastError: connected ? null : current.lastError, viewerFailure: connected ? null : current.viewerFailure });
                        return next;
                    });
                    setStreamVersions((versions) => new Map(versions).set(cameraKey, (versions.get(cameraKey) || 0) + 1));
                });
                // A connected peer can still deliver no usable video. The retry
                // budget resets only after frames are decoded, so a viewer that
                // keeps stalling ends in viewerFailure instead of looping.
                const pollDecodeStats = async () => {
                    const rtc = session.rtcPeerConnection;
                    if (!isCurrent() || !rtc || rtc.connectionState !== "connected") return;
                    if (document.visibilityState !== "visible") {
                        record.decodeState = null;
                        return;
                    }
                    let report;
                    try {
                        report = await rtc.getStats();
                    } catch (error) {
                        return;
                    }
                    if (!isCurrent()) return;
                    const sample = pickVideoInboundSample(report);
                    const result = evaluateDecodeStall(record.decodeState, sample, Date.now(), { stallMs: DECODE_STALL_MS });
                    record.decodeState = result.state;
                    if (result.decoding && !record.decodeConfirmed) {
                        record.decodeConfirmed = true;
                        logWebRTC("viewer-decoding", { cameraKey, sessionId: session.sessionId || null, decoder: sample.decoderImplementation });
                        sessionAttemptRef.current.set(cameraKey, 0);
                        const timer = sessionRetryTimerRef.current.get(cameraKey);
                        if (timer) clearTimeout(timer);
                        sessionRetryTimerRef.current.delete(cameraKey);
                    }
                    if (!result.stalled.length) return;
                    debugTap.report({ kind: "stall", cause: "decode-stall", cameraKey, connectionId: record.debug?.connectionId, reasons: result.stalled, framesReceived: sample.framesReceived, framesDecoded: sample.framesDecoded });
                    warnWebRTC("viewer-decode-stall", { cameraKey, sessionId: session.sessionId || null, reasons: result.stalled, ...sample });
                    updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: "No decodable video" });
                    closeConsumerSession(cameraKey, true);
                    scheduleSessionRetry(cameraKey, `No decodable video for ${cameraKey} (${result.stalled.join(", ")}).`);
                };
                const observePeerConnection = () => {
                    const rtc = session.rtcPeerConnection;
                    if (!rtc || rtc.__r2c2StateListener) return;
                    rtc.__r2c2StateListener = true;
                    record.debug = debugTap.attach({ cameraKey, rtc, sessionId: session.sessionId });
                    if (record.statsTimer) clearInterval(record.statsTimer);
                    record.decodeState = null;
                    record.statsTimer = window.setInterval(pollDecodeStats, DECODE_STATS_INTERVAL_MS);
                    const rtcConfig = rtc.getConfiguration();
                    logWebRTC("viewer-peer-created", { cameraKey, sessionId: session.sessionId || null, iceTransportPolicy: rtcConfig.iceTransportPolicy, iceServerCount: rtcConfig.iceServers?.length || 0 });
                    rtc.addEventListener("icegatheringstatechange", () => logWebRTC("viewer-ice-gathering", { cameraKey, state: rtc.iceGatheringState }));
                    rtc.addEventListener("iceconnectionstatechange", () => logWebRTC("viewer-ice-connection", { cameraKey, state: rtc.iceConnectionState }));
                    rtc.addEventListener("signalingstatechange", () => logWebRTC("viewer-sdp-state", { cameraKey, state: rtc.signalingState }));
                    rtc.addEventListener("icecandidate", (event) => logWebRTC("viewer-local-candidate", { cameraKey, candidate: event.candidate?.candidate || null, sdpMid: event.candidate?.sdpMid ?? null, endOfCandidates: !event.candidate }));
                    rtc.addEventListener("icecandidateerror", (event) => warnWebRTC("viewer-ice-candidate-error", { cameraKey, errorCode: event.errorCode, errorText: event.errorText }));
                    rtc.addEventListener("connectionstatechange", () => {
                        if (!isCurrent()) return;
                        logWebRTC("viewer-peer-connection", { cameraKey, state: rtc.connectionState });
                        if (rtc.connectionState === "failed" || rtc.connectionState === "disconnected") {
                            debugTap.report({ kind: "stall", cause: `peer-${rtc.connectionState}`, cameraKey, connectionId: record.debug?.connectionId });
                        }
                        if (rtc.connectionState === "failed") {
                            updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: `Peer connection ${rtc.connectionState}` });
                            closeConsumerSession(cameraKey, true);
                            scheduleSessionRetry(cameraKey, `WebRTC peer connection failed for ${cameraKey}.`);
                        } else if (rtc.connectionState === "disconnected") {
                            updateEntry(cameraKey, { status: StreamingState.Reconnecting, lastError: "Peer connection disconnected" });
                        } else if (rtc.connectionState === "connected" && mediaStreamsRef.current.has(cameraKey)) {
                            if (record.connectTimer) clearTimeout(record.connectTimer);
                            record.connectTimer = null;
                            updateEntry(cameraKey, { status: StreamingState.Okay, lastError: null, lastEventAt: Date.now() });
                        }
                    });
                };
                session.addEventListener("rtcPeerConnectionChanged", () => {
                    if (isCurrent()) observePeerConnection();
                });
                session.addEventListener("stateChanged", () => {
                    if (!isCurrent()) return;
                    logWebRTC("viewer-session-state", { cameraKey, state: session.state, sessionId: session.sessionId || null, hasPeerConnection: Boolean(session.rtcPeerConnection) });
                    observePeerConnection();
                });
                session.addEventListener("error", (event) => {
                    if (!isCurrent()) return;
                    const message = event.message || event.error?.message || "WebRTC viewer session failed";
                    warnWebRTC("viewer-error", { cameraKey, message });
                    updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: message });
                    closeConsumerSession(cameraKey, true);
                    scheduleSessionRetry(cameraKey, `WebRTC viewer failed for ${cameraKey}: ${message}`);
                });
                session.addEventListener("closed", () => {
                    if (!isCurrent()) return;
                    warnWebRTC("viewer-closed-unexpectedly", { cameraKey, sessionId: session.sessionId || null });
                    closeConsumerSession(cameraKey, true);
                    scheduleSessionRetry(cameraKey, `WebRTC viewer ended for ${cameraKey}.`);
                });
                if (!session.connect()) throw new Error(`Could not start a WebRTC viewer for ${cameraKey}.`);
                logWebRTC("viewer-start-session-sent", { cameraKey, producerId: record.producerId });
            }));
        };
        start().catch((error) => {
            const message = error?.message || String(error);
            warnWebRTC("viewer-start-failed", { cameraKeys: records.map(([cameraKey]) => cameraKey), message });
            records.forEach(([cameraKey, record]) => {
                if (record.cancelled || consumerSessionsRef.current.get(cameraKey) !== record) return;
                updateEntry(cameraKey, { status: StreamingState.Reconnecting, mediaStream: null, lastError: message });
                closeConsumerSession(cameraKey, true);
                scheduleSessionRetry(cameraKey, message);
            });
        });
    }, [desiredViewerEntries, catalog, clientPeerId, connectedToWebRTC, serverHost, arcId, isFleetMode, sessionRetry, updateEntry, closeConsumerSession, scheduleSessionRetry, debugTap]);

    useEffect(() => () => {
        removalTimersRef.current.forEach((timer) => clearTimeout(timer));
        removalTimersRef.current.clear();
        sessionRetryTimerRef.current.forEach((timer) => clearTimeout(timer));
        sessionRetryTimerRef.current.clear();
        consumerSessionsRef.current.forEach((record) => {
            record.cancelled = true;
            if (record.statsTimer) clearInterval(record.statsTimer);
            if (record.debug) record.debug.detach();
            if (record.session) record.session.close();
        });
        consumerSessionsRef.current.clear();
        mediaStreamsRef.current.forEach((stream) => stopUnsharedMediaTracks(stream));
        mediaStreamsRef.current.clear();
        if (apiRef.current) apiRef.current.close();
    }, []);

    // Preserve the existing object-recognition source and overlay polling.
    useEffect(() => {
        if (!serverHost) return undefined;
        let cancelled = false;
        fetchYoloRecognition(serverHost, isSecure, arcId)
            .then(({ enabled }) => { if (!cancelled) setObjectRecognitionEnabled(Boolean(enabled)); })
            .catch(() => { if (!cancelled) setObjectRecognitionEnabled(false); });
        return () => { cancelled = true; };
    }, [serverHost, isSecure, arcId]);

    useEffect(() => {
        if (!serverHost) return undefined;
        let cancelled = false;
        fetchInferSource(serverHost, isSecure, arcId)
            .then((payload) => { if (!cancelled) refreshInferenceSource(payload.source); })
            .catch(() => { if (!cancelled) refreshInferenceSource("front"); });
        return () => { cancelled = true; };
    }, [serverHost, isSecure, arcId, refreshInferenceSource]);

    useEffect(() => {
        if (!objectRecognitionEnabled && inferenceTargetStream) clearStreamDetections(inferenceTargetStream);
    }, [objectRecognitionEnabled, inferenceTargetStream, clearStreamDetections]);

    useEffect(() => {
        if (!serverHost || !objectRecognitionEnabled || !inferenceTargetStream) return undefined;
        let cancelled = false;
        let timer = null;
        const key = inferenceTargetStream;
        const tick = async () => {
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
            try {
                const url = getBboxDetectionsUrl(serverHost, isSecure, arcId);
                if (!url) return;
                const response = await authedFetch(url, { cache: "no-store", signal: controller.signal });
                if (!response.ok) throw new Error("bbox request failed");
                const data = await response.json();
                if (!cancelled && typeof data?.w === "number" && typeof data?.h === "number" && Array.isArray(data.boxes)) {
                    setStreamDetections((previous) => new Map(previous).set(key, { w: data.w, h: data.h, boxes: data.boxes }));
                }
            } catch { if (!cancelled) clearStreamDetections(key); }
            finally {
                clearTimeout(timeoutId);
                if (!cancelled) timer = window.setTimeout(tick, POLL_INTERVAL_MS);
            }
        };
        tick();
        return () => { cancelled = true; clearTimeout(timer); clearStreamDetections(key); };
    }, [serverHost, isSecure, arcId, objectRecognitionEnabled, inferenceTargetStream, clearStreamDetections]);

    useEffect(() => {
        if (!serverHost || !isThermalCameraStream(activeStream)) return undefined;
        let cancelled = false;
        let timer = null;
        const key = activeStream;
        const tick = async () => {
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => controller.abort(), POLL_TIMEOUT_MS);
            try {
                const url = getThermalDetectionsUrl(serverHost, isSecure, arcId);
                if (!url) return;
                const response = await authedFetch(url, { cache: "no-store", signal: controller.signal });
                if (!response.ok) throw new Error("thermal detections request failed");
                const data = await response.json();
                if (!cancelled && typeof data?.w === "number" && typeof data?.h === "number" && Array.isArray(data.boxes)) {
                    setStreamDetections((previous) => new Map(previous).set(key, { w: data.w, h: data.h, boxes: data.boxes }));
                }
            } catch { if (!cancelled) clearStreamDetections(key); }
            finally {
                clearTimeout(timeoutId);
                if (!cancelled) timer = window.setTimeout(tick, POLL_INTERVAL_MS);
            }
        };
        tick();
        return () => { cancelled = true; clearTimeout(timer); clearStreamDetections(key); };
    }, [serverHost, activeStream, isSecure, arcId, clearStreamDetections]);

    const dismissSignalingError = useCallback(() => setSignalingError(null), []);
    const value = useMemo(() => ({
        setWebRTCServerHost: setServerHost,
        connectedToWebRTC,
        availableStreams,
        streamDisplayNames,
        streamStatuses,
        viewerFailures,
        streamVersions,
        activeStream,
        setActiveStream,
        getMediaStream,
        attachMediaStreamToElem,
        streamDetections,
        objectRecognitionEnabled,
        setObjectRecognitionEnabled,
        inferenceSource,
        refreshInferenceSource,
        clearStreamDetections,
        pinnedPayloadStreams,
        togglePinnedPayloadStream,
        swapPinnedWithActive,
        showStreamingSource,
        setShowStreamingSource,
        fitScreen,
        setFitScreen,
        signalingError,
        dismissSignalingError,
        debugTap,
        arcSerial,
    }), [debugTap, arcSerial, connectedToWebRTC, availableStreams, streamDisplayNames, streamStatuses, viewerFailures, streamVersions, activeStream, getMediaStream, attachMediaStreamToElem, streamDetections, objectRecognitionEnabled, inferenceSource, refreshInferenceSource, clearStreamDetections, pinnedPayloadStreams, togglePinnedPayloadStream, swapPinnedWithActive, showStreamingSource, fitScreen, signalingError, dismissSignalingError]);

    return <WebRTCContext.Provider value={value}>{children}</WebRTCContext.Provider>;
};

export default WebRTCProvider;
